import React, { useEffect, useState } from 'react';
import {
    ArrowLeftOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    DollarOutlined,
    DownloadOutlined,
    EditOutlined,
    FileTextOutlined,
    HistoryOutlined,
    HomeOutlined,
    InfoCircleOutlined,
    MoreOutlined,
    PrinterOutlined,
    ReloadOutlined,
    SendOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import {
    Alert,
    Breadcrumb,
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    Dropdown,
    Empty,
    message,
    Modal,
    Progress,
    Row,
    Space,
    Spin,
    Table,
    Tag,
    Timeline,
    Typography,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { collectionAPIWithErrorHandling } from '../../../api/collection/collection';
import dayjs from 'dayjs';
import { round } from 'lodash';
import { invoiceAPIWithErrorHandling } from '../../../api/invoice/invoice';
import { downloadBlob } from '../../../utils/excel';
import SendZnsDialog from './SendZnsDialog';
import ExtendPaymentDialog from './ExtendPaymentDialog';
import { DeadlineTag, OverduePaymentAlert } from './CollectionDeadline';
import useReceiptExport from './useReceiptExport';
import { readSwrCache, writeSwrCache, SWR_MAX_AGE_SHORT } from '../../../utils/swrCache';

const { Title, Text } = Typography;

// Khoản thu CANCELLED đã bị hủy nên không còn là nghĩa vụ thu: không cộng vào tiền,
// không tính vào số khách/tỷ lệ của đơn vị.
const isBillableFee = (fee) => fee?.status !== 'CANCELLED';

// Tách riêng để đường cache và đường API dùng chung đúng một cách map.
//
// KHOẢN THU ≠ KHÁCH HÀNG: `total_customers` của backend thực ra là số KHOẢN (COUNT(*)),
// một khách nhiều tài sản có nhiều khoản. Số khách thật là `total_unique_customers`.
const mapCollectionStats = (statsData) => ({
    totalFees: statsData.total_fees ?? statsData.total_customers ?? 0,
    totalUniqueCustomers: statsData.total_unique_customers || 0,
    totalAmountDue: parseFloat(statsData.total_amount_due || 0),
    totalAmountPaid: parseFloat(statsData.total_amount_paid || 0),
    // Backend cộng GREATEST(...) theo từng dòng. KHÔNG tự tính totalAmountDue - totalAmountPaid:
    // amount_paid được phép vượt amount_due nên phép trừ trên số tổng sẽ để tiền thu vượt của
    // khách này cấn trừ mất tiền còn nợ của khách khác.
    totalOutstanding: parseFloat(statsData.total_outstanding || 0),
    totalOverpaid: parseFloat(statsData.total_overpaid || 0),
    totalOverpaidCustomers: statsData.total_overpaid_customers || 0,
    totalPending: statsData.total_pending || 0,
    totalPaid: statsData.total_paid || 0,
    totalOverdue: statsData.total_overdue || 0,
    collectionRate: statsData.collection_rate || 0,
    collectionOrg: statsData.total_organizations || 0,
});

export default function ParentCollectionDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [collection, setCollection] = useState(null);
    const [extendOpen, setExtendOpen] = useState(false);
    // SWR: seed khối thống kê từ cache của phiên (đọc đúng 1 lần lúc mount) để có số
    // ngay ở lần render đầu, rồi revalidate ở nền trong fetchCollectionStats().
    const statsCacheKey = `collection.stats.${id}`;
    const [stats, setStats] = useState(() => {
        const cached = readSwrCache(statsCacheKey, { maxAge: SWR_MAX_AGE_SHORT });
        return cached ? mapCollectionStats(cached) : {};
    });
    const [mockUnitStats, setMockUnitStats] = useState([]);
    // Đợt đang mở hộp thoại gửi tin (đợt con hoặc cả đợt cha); null = đóng
    const [sendTarget, setSendTarget] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    // Logic xuất phiếu thu dùng chung với trang chi tiết đơn vị - xem useReceiptExport.
    const { queueExport, exportingId, openHistory, historyModal } = useReceiptExport({ rootId: id });

    
  const canEditCollection =
    Boolean(collection) &&
    // OLD: chỉ cho chỉnh khi PLANNED
    // collection.status === "PLANNED" &&
    // NEW: cho chỉnh cả khi ONGOING, chặn khi CLOSED
    collection.status !== "CLOSED";

    // Fetch collection data on component mount 
    useEffect(() => {
        if (id) {
            fetchCollectionData();
            fetchCollectionStats();
        }
    }, [id]);

    const fetchCollectionData = async () => {
        try {
            setLoading(true);
            const response = await collectionAPIWithErrorHandling.getCollectionById(
                id
            );
            const collectionData = response.data?.data;

            if (!collectionData) {
                message.error("Không tìm thấy đợt thu");
                navigate("/collections");
                return;
            }

            setCollection(collectionData);
            // Bảng đơn vị giữ nguyên thứ tự backend trả về: tên đơn vị hiểu được số trong
            // tên (Khu phố 01 → 31), cùng quy tắc với phần mở rộng ở danh sách đợt thu.
            //
            // Trước đây sắp theo tỷ lệ thu thấp lên trước để dễ soi đơn vị chậm, nhưng hai
            // màn cạnh nhau ra hai thứ tự khác nhau thì khó dò theo khu phố hơn là lợi.
            setMockUnitStats(collectionData.children || []);
        } catch (error) {
            message.error("Không thể tải thông tin đợt thu: " + error.message);
            navigate("/collections");
        } finally {
            setLoading(false);
        }
    };

    const fetchCollectionStats = async () => {
        try {
            const response = await collectionAPIWithErrorHandling.getCollectionStats(
                id
            );
            const statsData = response.data?.data;

            if (statsData) {
                setStats(mapCollectionStats(statsData));
                writeSwrCache(statsCacheKey, statsData);
            }
        } catch (error) {
            console.error("Không thể tải thống kê:", error.message);
            // Không hiển thị lỗi để không làm gián đoạn UX, chỉ log.
            // Giữ nguyên stats đang vẽ (có thể là bản cache) thay vì xoá về 0.
        }
    };

    const getStatusInfo = () => {
        if (!collection) return { status: "default", text: "Không xác định", color: "gray" };

        const statusConfig = {
            PLANNED: {
                icon: <ClockCircleOutlined />,
                color: 'blue',
                text: 'Nháp',
            },
            ONGOING: {
                icon: <CheckCircleOutlined />,
                color: 'green',
                text: 'Đang diễn ra',
            },
            CLOSED: {
                icon: <CheckCircleOutlined />,
                color: 'red',
                text: 'Đã kết thúc',
            },
        };
        return statusConfig[collection.status];
    };

    const handleViewDetail = (record) => {
        message.info(`Chi tiết thông tin đợt thu: ${record.name}`);
        navigate(`/collections/information/${record.id}`);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("vi-VN", {
            style: "currency",
            currency: "VND",
        }).format(amount);
    };

    const getChildCollectionStats = (childRecord) => {
        // Khoản thu đã hủy không còn là nghĩa vụ thu nên phải loại trước khi cộng tiền,
        // khớp với cách backend tính ở CollectionService::getCollectionStats().
        const billableFees = (childRecord.customer_fees || []).filter(isBillableFee);

        if (billableFees.length === 0) {
            return {
                totalFees: 0,
                paidFees: 0,
                rate: 0,
                totalAmount: 0,
                paidAmount: 0,
                organizationName: childRecord.organization?.name || 'N/A'
            };
        }

        /*
         * Tiến độ theo KHOẢN THU, không theo khách. Bản cũ đếm khách (Set customer_id)
         * và coi khách là "đã nộp" chỉ cần MỘT khoản có tiền — khách 3 khoản mới trả 1
         * vẫn tính đã nộp, thổi tỷ lệ thu lên. Đếm theo khoản, "đã TT" = status PAID,
         * cùng định nghĩa với nút lọc "Đã TT" ở chi tiết đợt thu.
         */
        let paidFees = 0;
        let totalAmount = 0;
        let paidAmount = 0;

        billableFees.forEach(fee => {
            totalAmount += parseFloat(fee.amount_due || 0);
            paidAmount += parseFloat(fee.amount_paid || 0);
            if (fee.status === 'PAID') paidFees += 1;
        });

        const rate = round((paidFees / billableFees.length) * 100, 2) || 0;

        return {
            totalFees: billableFees.length,
            paidFees,
            rate,
            totalAmount,
            paidAmount,
            organizationName: childRecord.organization?.name || 'N/A'
        };
    };

    // Gửi tin: mỗi dòng là một ĐỢT CON nên cũng là gửi cả đợt — dùng chung hộp thoại
    // hai lựa chọn (SendZnsDialog) với nút "tất cả đơn vị", không tự chọn theo referentid.

    
    const handleExportInvoice = async (keyId, isOne = false) => {
        const messageKey = "invoice";
    
        try {
          message.loading({ key: messageKey, content: "Đang xuất hóa đơn..." });
          const res = isOne
            ? await invoiceAPIWithErrorHandling.exportAll(keyId)
            : await invoiceAPIWithErrorHandling.exportAllParent(id);
    
          const { success, message: msg, data } = res.data;
          if (!success) {
            throw new Error(msg || "Xuất hóa đơn thất bại");
          }
    
          const successCount = data?.success ?? 0;
          const failedCount = data?.failed ?? 0;
    
          // ✅ Toàn bộ thất bại
          if (successCount === 0 && failedCount > 0) {
            message.error({
              key: messageKey,
              content: `Xuất hóa đơn thất bại (${failedCount} khoản thu không thể xuất)`,
              duration: 5,
            });
            return;
          }
    
          // ⚠️ Thành công 1 phần
          if (failedCount > 0) {
            message.warning({
              key: messageKey,
              content: `Xuất hóa đơn hoàn tất: thành công ${successCount}, thất bại ${failedCount}`,
              duration: 6,
            });
            return;
          }
    
          // 🎉 Thành công toàn bộ
          message.success({
            key: messageKey,
            content: `Xuất hóa đơn thành công (${successCount} khoản thu)`,
          });
    
        } catch (error) {
          message.error({
            key: messageKey,
            content: error.message || "Không thể xuất hóa đơn",
          });
        }
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const response =
            await collectionAPIWithErrorHandling.exportCollectionParentByID(id);

            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const currentDate = new Date()
                .toISOString()
                .split("T")[0]
                .replace(/-/g, "_");
            const filename = `THONG_KE_DOT_THU_${currentDate}.xlsx`;

            downloadBlob(blob, filename);
            message.success(`Tải xuống chi tiết đợt thu thành công!`);
        } catch (error) {
            message.error(error.message || "Không thể tải xuống danh sách đợt thu");
        } finally {
            setIsExporting(false);
        }
    };

    // Table columns
    const columns = [
        {
            title: 'STT',
            key: 'index',
            width: 60,
            align: 'center',
            render: (_, __, index) => <Text>{index + 1}</Text>,
        },
        {
            title: 'Đơn vị',
            key: 'unit',
            width: "15%",
            render: (_, record) => {
                const stats = getChildCollectionStats(record);
                return (
                    <div>
                        <Text strong>{stats.organizationName}</Text>
                    </div>
                );
            },
        },
        {
            // Không tính khoản đã hủy
            title: 'Tổng khoản',
            key: 'total',
            width: "15%",
            align: 'center',
            render: (_, record) => {
                const stats = getChildCollectionStats(record);
                return <Text strong>{stats.totalFees}</Text>;
            },
        },
        {
            title: 'Khoản đã TT',
            key: 'paid',
            width: "15%",
            align: 'center',
            render: (_, record) => {
                const stats = getChildCollectionStats(record);
                return <Text type="success" strong>{stats.paidFees}</Text>;
            },
        },
        {
            title: 'Tỷ lệ thu',
            key: 'collection_rate',
            width: "20%",
            align: 'center',
            render: (_, record) => {
                const stats = getChildCollectionStats(record);
                return (
                    <div>
                        <Text strong className="text-purple-600">
                            {stats.rate}%
                        </Text>
                        <Progress
                            percent={stats.rate}
                            strokeColor="#757FE7"
                            size="small"
                            className="mt-1"
                            showInfo={false}
                        />
                    </div>
                );
            }
        },
        {
            title: 'Số tiền đã thu',
            key: 'amount',
            width: "20%",
            align: 'right',
            render: (_, record) => {
                const stats = getChildCollectionStats(record);
                return (
                    <div style={{ textAlign: 'right' }}>
                        <Text strong style={{ color: '#128555' }}>
                            {formatCurrency(stats.paidAmount)}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            / {formatCurrency(stats.totalAmount)}
                        </Text>
                    </div>
                );
            },
        },
        {
            title: 'Hành động',
            key: 'actions',
            width: "15%",
            fixed: 'right',
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="default"
                        size="small"
                        icon={<InfoCircleOutlined />}
                        onClick={() => handleViewDetail(record)}
                        title="Xem chi tiết"
                    />
                    <Button
                        type="default"
                        size="small"
                        icon={<SendOutlined />}
                        title="Gửi tin nhắc thanh toán cho đơn vị này"
                        disabled={["PAID", "CANCELLED"].includes(record.status) || collection.status === "PLANNED"}
                        onClick={() => setSendTarget({
                            id: record.id,
                            type: "collection",
                            name: record.organization?.name || record.name,
                        })}
                    />
                    <Button
                        type="default"
                        size="small"
                        icon={<PrinterOutlined />}
                        onClick={() => queueExport(record.id, "child")}
                        loading={exportingId === record.id}
                        title="Xuất phiếu thu"
                        disabled={["PAID", "CANCELLED"].includes(record.status) || collection.status === "PLANNED"}
                    />
                    <Button
                        type="default"
                        size="small"
                        icon={<FileTextOutlined />}
                        onClick={() => handleExportInvoice(record.id, true)}
                        title="Lập hóa đơn"
                        // disabled={["UNPAID", "PARTIAL"].includes(record.status) || collection.status === "PLANNED"}
                        disabled={true}
                    />
                </Space>
            ),
        },
    ];


    if (loading) {
        return (
            <div className="bg-gray-50 min-h-screen p-6 flex items-center justify-center"
                style={{
                    display: "flex",
                    justifyContent: "center",
                    height: "80vh",
                    alignItems: "center",
                }}>
                <Spin size="large" tip="Đang tải thông tin đợt thu..." />
            </div>
        );
    }

    if (!collection) {
        return (
            <div className="bg-gray-50 min-h-screen p-6 flex items-center justify-center">
                <Empty description="Không tìm thấy đợt thu" />
            </div>
        );
    }

    const statusInfo = getStatusInfo();
    
    const getChargeTargetTypeTag = (charge_target_type) => {
        const statusConfig = {
        CUSTOMER: { color: "blue", text: "Khách hàng" },
        APARTMENT: { color: "green", text: "Chung cư" },
        VEHICLE: { color: "red", text: "Bãi xe" },
        };

        const config = statusConfig[charge_target_type] || { color: "default", text: charge_target_type };
        return <Tag color={config.color} className="px-3 py-1">{config.text}</Tag>;
    };

    // Action


    const handleEdit = () => {
        navigate(`/collections/${id}`);
    };

    const handleDelete = async () => {
        const isActive = collection.status === 'ONGOING';
        
        Modal.confirm({
            title: `Xác nhận xóa chiến dịch tổng hợp?`,
            icon: <DeleteOutlined style={{ color: '#E5065C' }} />,
            content: (
                <div>
                    <p>Bạn đang yêu cầu xóa chiến dịch: <b>{collection.name}</b></p>
                    {isActive && (
                        <Alert 
                            type="error"
                            showIcon
                            message="Cảnh báo: Chiến dịch này đang diễn ra. Việc xóa sẽ gỡ bỏ toàn bộ dữ liệu quản lý liên quan!"
                            style={{ marginTop: 8 }}
                        />
                    )}
                    <p style={{ marginTop: 8 }}>Hành động này không thể hoàn tác. Bạn có chắc chắn không?</p>
                </div>
            ),
            okText: 'Xác nhận xóa',
            okType: 'danger',
            cancelText: 'Hủy',
            onOk: async () => {
                try {
                    await collectionAPIWithErrorHandling.deleteCollection(collection.id);
                    message.success("Xóa chiến dịch thành công");
                    navigate("/collections");
                } catch (error) {
                    message.error(error.message || "Không thể xóa đợt thu");
                }
            }
        });
    };
    
    // Handle reset
    const handleReset = () => {
        fetchCollectionData();
        fetchCollectionStats();
    };

    // Bảng "Tiến độ theo đơn vị" không phân trang: một phường vài chục khu phố là block
    // này dài vô hạn, đẩy nút hành động xuống tận cuối trang. Bật cuộn dọc (kèm header
    // dính sẵn của antd khi có scroll.y) thay vì phân trang để vẫn xem liền mạch được.
    // Chỉ bật khi thực sự dài: antd đặt cứng overflow-y:scroll cho phần thân bảng, bảng
    // ngắn mà bật sẽ lộ một máng scrollbar rỗng.
    const UNIT_ROWS_BEFORE_SCROLL = 10;
    const unitTableScrollY =
        mockUnitStats.length > UNIT_ROWS_BEFORE_SCROLL ? 460 : undefined;

    return (
        <div className="bg-gray-50 min-h-screen p-6">
            {/* <Card className="shadow-lg max-w-7xl mx-auto"> */}
            <Card className="shadow-lg mx-auto">
                {/* Breadcrumb */}
                <div className="mb-6">
                    <Breadcrumb
                        separator=">"
                        items={[
                            {
                                title: (
                                    <>
                                        <HomeOutlined /> Trang chủ
                                    </>
                                ),
                            },
                            {
                                title: (
                                    <>
                                        <DollarOutlined /> Chiến dịch thu
                                    </>
                                ),
                            },
                            {
                                title: (
                                    <>
                                        <InfoCircleOutlined /> Chi tiết tổng hợp
                                    </>
                                ),
                            },
                        ]}
                    />
                </div>

                {/* Header */}
                <div className="mb-6">
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Space align="start" direction="vertical" size={4}>
                                <div>
                                    <Title level={2} className="mb-2">
                                        {collection.name}
                                    </Title>
                                    <Space size={8}>
                                        <Tag color="purple" className="px-3 py-1"
                                            style={{ fontSize: '14px' }}
                                        >
                                            <TeamOutlined /> Chiến dịch tổng hợp
                                        </Tag>
                                        <Tag
                                            color={statusInfo.color}
                                            icon={statusInfo.icon}
                                            className="px-3 py-1"
                                        >
                                            {statusInfo.text}
                                        </Tag>
                                        {getChargeTargetTypeTag(collection.charge_target_type)}
                                        <DeadlineTag collection={collection} />
                                    </Space>
                                </div>
                            </Space>
                        </Col>
                        <Col>
                            {/* Cùng bố cục với InfoCollection: hai thao tác hay dùng để ngoài, còn lại vào "⋯". */}
                            <Space>
                                <Button
                                    type="primary"
                                    icon={<EditOutlined />}
                                    onClick={handleEdit}
                                    className="shadow-sm"
                                    disabled={!canEditCollection}
                                >
                                    Chỉnh sửa
                                </Button>
                                <Button
                                    icon={<PrinterOutlined />}
                                    onClick={() => queueExport(id, "parent")}
                                    className="shadow-sm"
                                    disabled={collection.status === 'PLANNED' || collection.status === 'CLOSED'}
                                >
                                    Xuất phiếu thu
                                </Button>
                                <Dropdown
                                    trigger={['click']}
                                    menu={{
                                        items: [
                                            {
                                                key: 'excel',
                                                icon: <DownloadOutlined />,
                                                label: isExporting ? 'Đang xuất Excel...' : 'Xuất Excel',
                                                disabled: isExporting || collection.status === 'PLANNED',
                                                onClick: handleExport,
                                            },
                                            { key: 'reload', icon: <ReloadOutlined />, label: 'Làm mới', onClick: handleReset },
                                            { type: 'divider' },
                                            { key: 'delete', icon: <DeleteOutlined />, label: 'Xóa đợt thu', danger: true, onClick: handleDelete },
                                        ],
                                    }}
                                >
                                    <Button icon={<MoreOutlined />} className="shadow-sm" aria-label="Thao tác khác" />
                                </Dropdown>
                            </Space>
                        </Col>
                    </Row>
                </div>

                <OverduePaymentAlert collection={collection} onExtend={() => setExtendOpen(true)} />

                <Divider />

                {/* BLOCK 1: Overview Statistics */}
                <Row gutter={[16, 16]} className="mb-6">
                    {/* Card 1: Tổng đơn vị */}
                    <Col xs={12} sm={8} lg={4}>
                        <Card className="shadow-sm h-full"
                            style={{ borderTop: '3px solid #656DC7', borderRadius: 8 }}
                            bodyStyle={{ padding: '16px' }}
                        >
                            <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>
                                <TeamOutlined style={{ marginRight: 4, color: '#656DC7' }} /> Tổng đơn vị
                            </div>
                            <div style={{ fontSize: 26, fontWeight: 700, color: '#656DC7', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                                {stats.collectionOrg || 0}
                                <span style={{ fontSize: 13, fontWeight: 400, marginLeft: 4 }}>đv</span>
                            </div>
                        </Card>
                    </Col>

                    {/* Card 2: Tổng khoản thu (một khách nhiều tài sản = nhiều khoản) */}
                    <Col xs={12} sm={8} lg={4}>
                        <Card className="shadow-sm h-full"
                            style={{ borderTop: '3px solid #0070D7', borderRadius: 8 }}
                            bodyStyle={{ padding: '16px' }}
                        >
                            <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>
                                <TeamOutlined style={{ marginRight: 4, color: '#0070D7' }} /> Tổng khoản thu
                            </div>
                            <div style={{ fontSize: 26, fontWeight: 700, color: '#0070D7', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                                {(stats.totalFees || 0).toLocaleString('vi-VN')}
                            </div>
                            {/* Cache SWR cũ chưa có số khách thì bỏ qua thay vì in "0 khách hàng" */}
                            {stats.totalUniqueCustomers > 0 && (
                                <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
                                    {stats.totalUniqueCustomers.toLocaleString('vi-VN')} khách hàng
                                </div>
                            )}
                        </Card>
                    </Col>

                    {/* Card 3: Tổng tiền */}
                    <Col xs={12} sm={8} lg={5}>
                        <Card className="shadow-sm h-full"
                            style={{ borderTop: '3px solid #0070D7', borderRadius: 8 }}
                            bodyStyle={{ padding: '16px' }}
                        >
                            <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>
                                <DollarOutlined style={{ marginRight: 4, color: '#0070D7' }} /> Tổng phải thu
                            </div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#0070D7', lineHeight: 1.3, fontVariantNumeric: 'tabular-nums' }}>
                                {formatCurrency(stats.totalAmountDue)}
                            </div>
                        </Card>
                    </Col>

                    {/* Card 4: Đã thu */}
                    <Col xs={12} sm={8} lg={5}>
                        <Card className="shadow-sm h-full"
                            style={{ borderTop: '3px solid #128555', borderRadius: 8 }}
                            bodyStyle={{ padding: '16px' }}
                        >
                            <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>
                                <DollarOutlined style={{ marginRight: 4, color: '#128555' }} /> Đã thu
                            </div>
                            {(() => {
                                // Tỷ lệ theo TIỀN, để khớp với hai con số ngay cạnh nó (đã thu /
                                // tổng phải thu). stats.collectionRate của backend là tỷ lệ theo
                                // SỐ KHOẢN đã thanh toán — đợt 125 ra 20,86% trong khi theo tiền
                                // là 18,64%, đặt dưới một con số tiền thì đọc ra nghĩa khác.
                                const paidRate = stats.totalAmountDue > 0
                                    ? (stats.totalAmountPaid / stats.totalAmountDue) * 100
                                    : 0;

                                return (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                                            <span style={{ fontSize: 16, fontWeight: 700, color: '#128555', lineHeight: 1.3, fontVariantNumeric: 'tabular-nums' }}>
                                                {formatCurrency(stats.totalAmountPaid)}
                                            </span>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: '#64748B', fontVariantNumeric: 'tabular-nums' }}>
                                                {paidRate.toFixed(1)}%
                                            </span>
                                        </div>
                                        <Progress
                                            percent={Number(paidRate.toFixed(1))}
                                            strokeColor="#16A66A"
                                            className="mt-2"
                                            size="small"
                                            showInfo={false}
                                        />
                                    </>
                                );
                            })()}
                        </Card>
                    </Col>

                    {/* Card 5: Còn lại */}
                    <Col xs={24} sm={16} lg={6}>
                        {(() => {
                            // Lấy từ backend, không tự trừ trên số tổng — xem chú thích ở mapCollectionStats.
                            const remainingAmount = stats.totalOutstanding || 0;
                            const overpaidAmount = stats.totalOverpaid || 0;
                            const isComplete = remainingAmount <= 0;
                            return (
                                <Card className="shadow-sm h-full"
                                    style={{
                                        borderTop: `3px solid ${remainingAmount > 0 ? '#E5065C' : '#128555'}`,
                                        borderRadius: 8,
                                        background: remainingAmount > 0 ? '#fff7f7' : '#EAF9F2',
                                    }}
                                    bodyStyle={{ padding: '16px' }}
                                >
                                    <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>
                                        <DollarOutlined style={{ marginRight: 4, color: remainingAmount > 0 ? '#E5065C' : '#128555' }} />
                                        Còn lại
                                    </div>
                                    <div style={{
                                        fontSize: 16,
                                        fontWeight: 700,
                                        color: remainingAmount > 0 ? '#E5065C' : '#128555',
                                        lineHeight: 1.3,
                                        fontVariantNumeric: 'tabular-nums'
                                    }}>
                                        {formatCurrency(remainingAmount)}
                                    </div>
                                    {isComplete && (
                                        <div style={{ fontSize: 11, color: '#128555', marginTop: 2 }}>✓ Hoàn thành</div>
                                    )}
                                    {/* Chỉ hiện khi thực sự có tiền thu vượt — không thêm nhiễu khi = 0. */}
                                    {overpaidAmount > 0 && (
                                        <div style={{ fontSize: 11, color: '#A8681E', marginTop: 2 }}>
                                            Thu vượt {formatCurrency(overpaidAmount)}
                                            {stats.totalOverpaidCustomers > 0 && ` · ${stats.totalOverpaidCustomers} khoản`}
                                        </div>
                                    )}
                                </Card>
                            );
                        })()}
                    </Col>
                </Row>

                {/* Biểu đồ đóng góp theo đơn vị */}

                {/* Thông tin chiến dịch và Timeline */}
                <Row gutter={[24, 16]} className="mb-6">
                    <Col xs={24} lg={16}>
                        <Card
                            title={
                                <span className="text-blue-600" 
                                    // style={{ color: '#667eea' }}
                                >
                                    <CalendarOutlined className="mr-2" />
                                    Thông tin chiến dịch
                                </span>
                            }
                            className="shadow-sm border-blue-200 h-full"
                        >
                            <Descriptions column={2} bordered size="middle">
                                <Descriptions.Item span={2} label="Tên chiến dịch">
                                    <Text  strong className="text-gray-800">{collection.name}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Mã chiến dịch">
                                    <Tag color="blue" className="text-sm px-3 py-1">{collection.code}</Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Loại phí">
                                    <Text className="text-gray-800">
                                        {collection.category?.name || "—"}
                                    </Text>
                                </Descriptions.Item>
                                {/* <Descriptions.Item label="T/K phát hành hóa đơn">
                                    <Space>
                                        <Text className="text-gray-800" strong>
                                            {collection?.invoice_cnf?.name}
                                        </Text>
                                    </Space>
                                </Descriptions.Item> */}
                                <Descriptions.Item label="Ngày bắt đầu">
                                    <Space>
                                        <CalendarOutlined className="text-blue-500" />
                                        <Text className="text-gray-800">
                                            {dayjs(collection.start_date).format("DD/MM/YYYY")}
                                        </Text>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="Ngày kết thúc">
                                    <Space>
                                        <CalendarOutlined />
                                        <Text className="text-gray-800">
                                            {dayjs(collection.end_date).format("DD/MM/YYYY")}
                                        </Text>
                                    </Space>
                                </Descriptions.Item>
                                {collection.payment_exp_date && (
                                    <Descriptions.Item label="Hạn giao dịch (đã gia hạn)">
                                        <Space>
                                            <ClockCircleOutlined />
                                            <Text>{dayjs(collection.payment_exp_date).format("DD/MM/YYYY")}</Text>
                                        </Space>
                                    </Descriptions.Item>
                                )}
                                {/* <Descriptions.Item label="Thời gian diễn ra">
                                    <Text>
                                        {dayjs(collection.end_date).diff(
                                            dayjs(collection.start_date),
                                            "day"
                                        ) + 1}{" "}
                                        ngày
                                    </Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Giờ gửi tin nhắc">
                                    <Space>
                                        <ClockCircleOutlined className="text-purple-500" />
                                        <Text className="text-gray-800">
                                            {collection.send_schedule_at
                                                ? dayjs(collection.send_schedule_at, "HH:mm:ss").format("HH:mm:ss")
                                                : "—"}
                                        </Text>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="Sau hạn">
                                    <Text>{collection.after_date ?? "—"}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Trước hạn">
                                    <Text>{collection.before_date ?? "—"}</Text>
                                </Descriptions.Item> */}
                                
                                <Descriptions.Item label="Hạn thu(tin nhắn nhận)">
                                    <Space>
                                    <ClockCircleOutlined />
                                    <Text>
                                        {collection.send_date
                                        ? dayjs(collection.send_date).format("DD/MM/YYYY")
                                        : "—"}
                                    </Text>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item span={2} label="Các khoản thu">
                                    {/* fee_definitions không chứa đơn giá (đơn giá nằm ở từng
                                        khoản thu của khách) nên chỉ liệt kê tên. Đa số đợt để
                                        nguyên K1/K2/K3, nhưng có đợt đặt tên thật như "CTRSH". */}
                                    {collection.fee_definitions?.length > 0 ? (
                                        <Text className="text-gray-800">
                                            {collection.fee_definitions
                                                .map((fee) => fee.name || fee.id)
                                                .join(" · ")}
                                        </Text>
                                    ) : (
                                        <Text type="secondary">—</Text>
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item span={2} label="Mô tả">
                                    <Text>{collection.description ?? "—"}</Text>
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Col>

                    <Col xs={24} lg={8}>
                        <Card
                            title={
                                <span className="text-purple-600"  
                                    // style={{ color: '#764ba2' }}
                                >
                                    <ClockCircleOutlined className="mr-2" />
                                    Lịch sử thay đổi
                                </span>
                            }
                            className="shadow-sm border-purple-200 h-full"
                        >
                            <Timeline>
                                <Timeline.Item color="green" dot={<CheckCircleOutlined style={{ fontSize: '16px' }} />}>
                                    <Text strong>Tạo chiến dịch</Text>
                                    <br />
                                    <Text type="secondary">
                                        {new Date(collection.created_at).toLocaleString('vi-VN')}
                                    </Text>
                                </Timeline.Item>

                                {collection.updated_at !== collection.created_at && (
                                    <Timeline.Item color="blue" dot={<EditOutlined style={{ fontSize: '16px' }} />}>
                                        <Text strong>Cập nhật lần cuối</Text>
                                        <br />
                                        <Text type="secondary">
                                            {new Date(collection.updated_at).toLocaleString('vi-VN')}
                                        </Text>
                                    </Timeline.Item>
                                )}

                                <Timeline.Item color={statusInfo.color} dot={statusInfo.icon}>
                                    <Text strong>Trạng thái hiện tại</Text>
                                    <br />
                                    <Tag color={statusInfo.color} style={{ marginTop: '4px' }}>
                                        {statusInfo.text}
                                    </Tag>
                                </Timeline.Item>
                            </Timeline>
                        </Card>
                    </Col>
                </Row>

                {/* BLOCK 2: Tiến độ theo đơn vị (Table) */}
                <Card
                    className="shadow-md border-0 rounded-xl"
                    title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span className="text-orange-600 font-semibold" 
                                // style={{ color: '#f5576c' }}
                            >
                                <FileTextOutlined className="mr-2" />
                                Tiến độ theo đơn vị 
                                
                                <Button
                                    type="default"
                                    size="small"
                                    icon={<SendOutlined />}
                                    onClick={() => setSendTarget({ id: collection.id, type: "parent", name: "tất cả đơn vị" })}
                                    style={{marginLeft : "10px"}}
                                    disabled={collection.status === "PLANNED"}
                                >
                                    Gửi tin (tất cả đơn vị)
                                </Button>
                            </span>
                            <Space size="middle">
                                <Button
                                    size="small"
                                    icon={<HistoryOutlined />}
                                    onClick={openHistory}
                                >
                                    Lịch sử xuất phiếu thu
                                </Button>
                                <Text type="secondary" style={{ fontSize: 14 }}>
                                    Tổng: <strong>{stats.collectionOrg}</strong> đơn vị
                                </Text>
                            </Space>
                        </div>
                    }
                >
                    <Table
                        columns={columns}
                        dataSource={mockUnitStats}
                        rowKey="id"
                        size="middle"
                        scroll={{ x: 'max-content', y: unitTableScrollY }}
                        pagination={false}
                        className="rounded-lg"
                        rowClassName="hover:bg-blue-50/50 transition-colors"
                    />
                </Card>

                {/* Action Buttons */}
                <Card className="mt-6 shadow-sm bg-gradient-to-r from-blue-50 to-purple-50">
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Button
                                icon={<ArrowLeftOutlined />}
                                // onClick={() => alert('Quay lại danh sách chiến dịch')}
                                onClick={() => navigate(-1)}
                                size="default"
                            >
                                Quay lại
                            </Button>
                        </Col>
                        <Col>
                            <Space>
                                <Button
                                    icon={<FileTextOutlined />}
                                    onClick={handleExportInvoice}
                                    size="default"
                                    className="shadow-sm"
                                    // disabled={collection.status === 'PLANNED'}
                                    disabled={true}
                                >
                                    Lập nhiều hóa đơn
                                </Button>
                                <Button
                                    icon={<PrinterOutlined />}
                                    onClick={() => queueExport(id, "parent")}
                                    size="default"
                                    className="shadow-sm"
                                    disabled={collection.status === 'PLANNED' || collection.status === 'CLOSED'}
                                >
                                    Xuất phiếu thu
                                </Button>
                                <Button
                                    icon={<DownloadOutlined />}
                                    onClick={handleExport}
                                    loading={isExporting}
                                    size="default"
                                    className="shadow-sm"
                                    disabled={collection.status === 'PLANNED'}
                                >
                                    Xuất Excel
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<EditOutlined />}
                                    onClick={handleEdit}
                                    size="default"
                                    className="shadow-sm bg-gradient-to-r from-blue-500 to-blue-600 border-none"
                                    // disabled={collection.status !== 'PLANNED'}
                                    disabled={!canEditCollection}
                                >
                                    Chỉnh sửa
                                </Button>
                                <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={handleDelete}
                                    className="shadow-sm"
                                >
                                    Xóa
                                </Button>
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={handleReset}
                                >
                                    Làm mới
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>
            </Card>

            {historyModal}

            <SendZnsDialog target={sendTarget} onClose={() => setSendTarget(null)} />
            <ExtendPaymentDialog
                open={extendOpen}
                collection={collection}
                onClose={() => setExtendOpen(false)}
                onDone={() => {
                    fetchCollectionData();
                    fetchCollectionStats();
                }}
            />
        </div>
    );
}
